import { router } from "@inertiajs/react";
import { messageReplyReactionPath } from "@travily/routes";
import type { MessageReaction } from "@ui/components/ReactionPicker";
import { useCallback, useMemo, useState } from "react";

type ReactionsByMessage = Record<string, MessageReaction[]>;

// Module-scoped so the default keeps one identity across renders, as a caller's own list must.
const THREAD_RELOAD_PROPS = ["reactions", "flash"];

/**
 * Reaction tallies for the open thread, with an optimistic toggle. Shared by the messages page and
 * the dock, which differ only in which props carry their tallies back.
 *
 * Add and remove are separate verbs rather than one toggling POST: a request whose effect flips on
 * hidden server state is unsafe to retry, and a lost response on a flaky connection would silently
 * undo the reaction. `reacted` on the wire is exactly which verb to send. The optimistic tally is
 * what stops a second tap re-sending the same verb and drawing a 409.
 *
 * The reload asks for the caller's own tallies plus `flash` — riding the reply-create redirect
 * would re-serialise the whole thread on every tap, but the controller's failure paths (409
 * conflict, 422) speak only through the flash alert, so dropping it would roll the pill back
 * unexplained.
 */
export function useMessageReactions(
  conversationSqid: string | null,
  serverReactions: ReactionsByMessage,
  reloadProps: string[] = THREAD_RELOAD_PROPS,
) {
  const [pending, setPending] = useState<ReactionsByMessage>({});
  const reactions = useMemo(() => ({ ...serverReactions, ...pending }), [serverReactions, pending]);

  const toggle = useCallback(
    (messageId: number, emoji: string) => {
      // Optimistic ids are negative and have no server row to react to yet, and a dock whose first
      // message is still unsent has no thread to address.
      if (messageId < 0 || !conversationSqid) return;

      const current = reactions[String(messageId)] ?? [];
      const mine = current.find((reaction) => reaction.emoji === emoji);
      const adding = !mine?.reacted;

      setPending((previous) => ({ ...previous, [String(messageId)]: nextTally(current, emoji, adding) }));

      const path = messageReplyReactionPath(conversationSqid, messageId);
      const options = {
        // Async so a mid-send toggle can't abort an in-flight reply POST — Inertia
        // cancels the active sync visit when another sync visit starts.
        async: true,
        preserveScroll: true,
        preserveState: true,
        only: reloadProps,
        onFinish: () => setPending((previous) => omit(previous, String(messageId))),
      };

      if (adding) router.post(path, { emoji }, options);
      else router.delete(`${path}?emoji=${encodeURIComponent(emoji)}`, options);
    },
    [conversationSqid, reactions, reloadProps],
  );

  return { reactions, toggle };
}

function nextTally(current: MessageReaction[], emoji: string, adding: boolean): MessageReaction[] {
  if (adding) {
    const existing = current.find((reaction) => reaction.emoji === emoji);
    return existing
      ? current.map((reaction) =>
          reaction.emoji === emoji ? { ...reaction, count: reaction.count + 1, reacted: true } : reaction,
        )
      : [...current, { emoji, count: 1, reacted: true }];
  }

  return current.flatMap((reaction) => {
    if (reaction.emoji !== emoji) return [reaction];
    const count = reaction.count - 1;
    return count > 0 ? [{ ...reaction, count, reacted: false }] : [];
  });
}

function omit(tallies: ReactionsByMessage, key: string): ReactionsByMessage {
  const { [key]: _dropped, ...rest } = tallies;
  return rest;
}
